// ---------------------------------------------------------------------------
// The column lists both directions of the sync agree on, and the projection
// that applies them on the way out.
//
// Separate from sync.ts, and pure, for the same reason push-errors.ts is:
// getting it wrong strands a canvasser's work, and sync.ts cannot be imported
// by a test because it reaches for the Supabase client.
// ---------------------------------------------------------------------------

export const HOUSEHOLD_COLUMNS =
  'id,street_address,unit,city,state,postal_code,lat,lon,source,source_id,turf_id,do_not_contact,do_not_contact_reason,do_not_contact_at,do_not_contact_by,active,created_at,updated_at';

export const VISIT_COLUMNS =
  'id,household_id,canvasser_id,visited_at,received_at,outcome,inaccessible_reason,' +
  'literature,best_return_time,sign_requested,volunteer_interest,notes';

export const PERSON_COLUMNS =
  'id,household_id,external_id,first_name,middle_name,last_name,suffix,party,gender,' +
  'age,age_as_of,registration_status,vbm_status,precinct,tier,turnout_score,' +
  'cell_phone,landline,do_not_call,callable,mailable,deceased,moved,active,' +
  'created_at,updated_at';

export const NOTE_COLUMNS = 'id,household_id,author_id,body,created_at,received_at';

export const CONTACT_COLUMNS =
  'id,visit_id,person_id,canvasser_id,contacted_at,received_at,spoke,support,' +
  'sign_requested,notes';

/**
 * What a queued row may carry to the server: the list the pull already
 * selects, so the two directions cannot drift apart, minus `received_at`,
 * which the server stamps.
 */
export function pushable(columns: string): readonly string[] {
  return columns.split(',').filter((c) => c !== 'received_at');
}

export const PUSH_COLUMNS = {
  visits: pushable(VISIT_COLUMNS),
  contacts: pushable(CONTACT_COLUMNS),
} as const;

/**
 * Project a queued row onto the columns the server actually has.
 *
 * A whitelist, and that is the whole point. The push used to delete the four
 * device-only fields and forward whatever was left, which quietly assumed the
 * row had been written by the same build that was pushing it. IndexedDB
 * outlives a build. A person contact queued before 0eb12c8 carries
 * `sign_delivered`, a column 20260908000700 then dropped, and PostgREST
 * rejects the whole batch with PGRST204 -- so one row recorded at a door
 * stalls not just its own upload but every sync after it, pull included.
 *
 * pushNotes() has always listed its fields explicitly. This is that, made
 * general: an unknown key is dropped rather than forwarded, and a key the row
 * does not have is omitted rather than sent as undefined, so a row written
 * before a column existed takes the server's default for it.
 */
export function toServerRow<Row>(row: object, columns: readonly string[]): Row {
  const source = row as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of columns) {
    if (key in source) out[key] = source[key];
  }
  // The only cast here. The caller names the generated insert type and the
  // column list is derived from the same schema, so the two move together.
  return out as Row;
}
